import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import BestBetsTakeover from './BestBetsTakeover';
import { BOOKMAKERS_LIST, isExcludedFromBestBook } from '../utils/constants';
import { formatGameTime } from '../utils/formatters';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useBooks } from '../contexts/BooksContext';
import '../styles/BestBets.css';

const BestBetsModal = ({ show, onHide, bestBets, getTeamLogoUrl, selectedSport }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedMarket, setSelectedMarket] = useState('all');
  const [selectedPoint, setSelectedPoint] = useState('all');
  const [selectedBook, setSelectedBook] = useState('all');
  const [selectedGame, setSelectedGame] = useState('all');
  const [selectedConference, setSelectedConference] = useState('all');
  const [minOdds, setMinOdds] = useState(null);
  const [maxOdds, setMaxOdds] = useState(null);
  const [minOddsInput, setMinOddsInput] = useState(''); // Local input state for debouncing
  const [maxOddsInput, setMaxOddsInput] = useState(''); // Local input state for debouncing
  // Tier filter state - which tiers are enabled (green=edge-one, orange=edge-two, blue=edge-three)
  const [enabledTiers, setEnabledTiers] = useState({
    'edge-one': true,   // Green (≥25%)
    'edge-two': true,   // Orange (≥15%)
    'edge-three': true  // Blue (≥5%)
  });
  // Hide bet365 toggle for NCAAB (due to inconsistent alt lines)
  const [hideBet365, setHideBet365] = useState(false);
  const [showBet365Tooltip, setShowBet365Tooltip] = useState(false);
  const { isDarkMode } = useDarkMode();
  const { bookOrder, bestBetsMaxDesktop, isBookEnabled } = useBooks();
  const hasInitialized = useRef(false);
  const minOddsDebounceRef = useRef(null);
  const maxOddsDebounceRef = useRef(null);

  // Market type options for the dropdown (only for ML best bets)
  const marketTypes = [
    { value: 'all', label: 'All Markets' },
    { value: 'moneyline', label: 'Moneyline' },
    { value: 'spreads', label: 'Spreads' },
    { value: 'totals', label: 'Totals' },
    { value: 'halves', label: '1st Half' },
    { value: 'quarters', label: '1st Qtr' }
  ];

  // Check if this is ML best bets (has mainline market types)
  const isMainlineBestBets = bestBets.some(bet => {
    const market = bet.market?.toLowerCase() || '';
    return market === 'moneyline' || market === 'h2h' ||
           market === 'spread' || market === 'spreads' || market === 'alt spread' ||
           market === 'totals' || market === 'total' || market === 'alt total' ||
           market === '1st half ml' || market === '1st qtr ml';
  });

  // Helper to check if a bet matches a market type
  const betMatchesMarketType = (bet, marketType) => {
    if (marketType === 'all') return true;
    const market = bet.market?.toLowerCase() || '';
    if (marketType === 'moneyline') {
      return market === 'moneyline' || market === 'h2h';
    }
    if (marketType === 'spreads') {
      return market === 'spread' || market === 'spreads' || market === 'alt spread';
    }
    if (marketType === 'totals') {
      return market === 'totals' || market === 'total' || market === 'alt total';
    }
    if (marketType === 'halves') {
      return market === '1st half ml' || market.includes('half');
    }
    if (marketType === 'quarters') {
      return market === '1st qtr ml' || market.includes('qtr') || market.includes('quarter');
    }
    return true;
  };

  // Parse odds string to number (e.g., "+150" -> 150, "-110" -> -110)
  const parseOdds = (oddsStr) => {
    if (!oddsStr) return null;
    return parseInt(oddsStr.replace('+', ''), 10);
  };

  // Get odds range from bestBets
  const oddsValues = bestBets
    .map(bet => parseOdds(bet.odds))
    .filter(odds => odds !== null && !isNaN(odds));
  const oddsMin = oddsValues.length > 0 ? Math.min(...oddsValues) : -200;
  const oddsMax = oddsValues.length > 0 ? Math.max(...oddsValues) : 300;

  // Sync input state when minOdds/maxOdds change (e.g., from slider)
  useEffect(() => {
    setMinOddsInput(minOdds !== null ? String(minOdds) : '');
  }, [minOdds]);

  useEffect(() => {
    setMaxOddsInput(maxOdds !== null ? String(maxOdds) : '');
  }, [maxOdds]);

  // Debounced handler for min odds input
  const handleMinOddsInputChange = (value) => {
    setMinOddsInput(value);
    if (minOddsDebounceRef.current) {
      clearTimeout(minOddsDebounceRef.current);
    }
    minOddsDebounceRef.current = setTimeout(() => {
      const val = parseInt(value, 10);
      if (!isNaN(val)) {
        setMinOdds(val);
      }
    }, 500);
  };

  // Debounced handler for max odds input
  const handleMaxOddsInputChange = (value) => {
    setMaxOddsInput(value);
    if (maxOddsDebounceRef.current) {
      clearTimeout(maxOddsDebounceRef.current);
    }
    maxOddsDebounceRef.current = setTimeout(() => {
      const val = parseInt(value, 10);
      if (!isNaN(val)) {
        setMaxOdds(val);
      }
    }, 500);
  };

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    return () => {
      if (minOddsDebounceRef.current) clearTimeout(minOddsDebounceRef.current);
      if (maxOddsDebounceRef.current) clearTimeout(maxOddsDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset filters when modal closes, initialize only once when it opens
  useEffect(() => {
    if (show && bestBets.length > 0 && !hasInitialized.current) {
      // Initialize odds to data range when modal first opens
      setMinOdds(oddsMin);
      setMaxOdds(oddsMax);
      hasInitialized.current = true;
    } else if (!show) {
      // Reset all filters when modal closes
      setSelectedMarket('all');
      setSelectedPoint('all');
      setSelectedBook('all');
      setSelectedGame('all');
      setSelectedConference('all');
      setMinOdds(null);
      setMaxOdds(null);
      setEnabledTiers({ 'edge-one': true, 'edge-two': true, 'edge-three': true });
      hasInitialized.current = false;
    }
  }, [show, bestBets.length, oddsMin, oddsMax]);

  // Reset line filter when market changes (since lines are market-specific)
  useEffect(() => {
    setSelectedPoint('all');
  }, [selectedMarket]);

  // Get unique point values from bestBets, filtered by selected market, sorted numerically
  const uniquePoints = [...new Set(
    bestBets
      .filter(bet => bet.point !== null && bet.point !== undefined)
      .filter(bet => betMatchesMarketType(bet, selectedMarket))
      .map(bet => bet.point)
  )].sort((a, b) => a - b);

  // Get unique books from bestBets (the "best" book for each bet) and count per book.
  // Also include Secondary books from secondaryMatches so Kalshi etc. show up as
  // filter options when they have an active edge on at least one row.
  const bookCounts = {};
  bestBets.forEach(bet => {
    if (bet.book) {
      const book = bet.book.toLowerCase();
      bookCounts[book] = (bookCounts[book] || 0) + 1;
    }
    if (Array.isArray(bet.secondaryMatches)) {
      bet.secondaryMatches.forEach(s => {
        const sBook = String(s.book).toLowerCase();
        // Only include visible Secondary books (skip hidden ones).
        if (!isBookEnabled(sBook)) return;
        bookCounts[sBook] = (bookCounts[sBook] || 0) + 1;
      });
    }
  });
  const uniqueBooks = Object.keys(bookCounts).sort();

  // Get unique games from bestBets
  const uniqueGames = [...new Set(
    bestBets
      .filter(bet => bet.game)
      .map(bet => bet.game)
  )].sort();

  // Get unique conferences from bestBets (for NCAAB)
  const uniqueConferences = [...new Set(
    bestBets
      .flatMap(bet => [bet.homeConference, bet.awayConference])
      .filter(conf => conf)
  )].sort();

  // Helper to check if a bet involves a Top 25 team
  const betInvolvesTop25 = (bet) => {
    return (bet.homeRank && bet.homeRank <= 25) || (bet.awayRank && bet.awayRank <= 25);
  };

  // Helper to check if a bet matches the selected conference
  const betMatchesConference = (bet, conference) => {
    if (conference === 'all') return true;
    if (conference === 'top25') return betInvolvesTop25(bet);
    return bet.homeConference === conference || bet.awayConference === conference;
  };

  // Helper to toggle a tier on/off
  const toggleTier = (tier) => {
    setEnabledTiers(prev => ({ ...prev, [tier]: !prev[tier] }));
  };

  // Check if any tier filter is active (not all enabled)
  const tierFilterActive = !enabledTiers['edge-one'] || !enabledTiers['edge-two'] || !enabledTiers['edge-three'];

  // Filter bestBets based on selected filters
  const filteredBets = bestBets.filter(bet => {
    const matchesMarket = betMatchesMarketType(bet, selectedMarket);
    const matchesConference = betMatchesConference(bet, selectedConference);
    const matchesPoint = selectedPoint === 'all' || bet.point === parseFloat(selectedPoint);
    const matchesBook = selectedBook === 'all'
      || bet.book.toLowerCase() === selectedBook
      || (bet.secondaryMatches && bet.secondaryMatches.some(s => String(s.book).toLowerCase() === selectedBook));
    const matchesGame = selectedGame === 'all' || bet.game === selectedGame;
    const betOdds = parseOdds(bet.odds);
    const matchesMinOdds = minOdds === null || betOdds === null || betOdds >= minOdds;
    const matchesMaxOdds = maxOdds === null || betOdds === null || betOdds <= maxOdds;
    // Tier filter - check if bet's tier is enabled
    const matchesTier = bet.tier ? enabledTiers[bet.tier] : true;
    // Hide bet365 filter for NCAAB
    const matchesBet365Filter = !hideBet365 || bet.book.toLowerCase() !== 'bet365';
    return matchesMarket && matchesConference && matchesPoint && matchesBook && matchesGame && matchesMinOdds && matchesMaxOdds && matchesTier && matchesBet365Filter;
  });

  const oddsFilterActive = minOdds !== null && maxOdds !== null && (minOdds !== oddsMin || maxOdds !== oddsMax);
  const isFiltered = selectedMarket !== 'all' || selectedPoint !== 'all' || selectedBook !== 'all' || selectedGame !== 'all' || selectedConference !== 'all' || oddsFilterActive || tierFilterActive || hideBet365;

  // Apply max results limit from settings (desktop)
  const displayedBets = bestBetsMaxDesktop !== null
    ? filteredBets.slice(0, bestBetsMaxDesktop)
    : filteredBets;
  const isLimited = bestBetsMaxDesktop !== null && filteredBets.length > bestBetsMaxDesktop;

  // Mobile: Use takeover component
  if (isMobile) {
    return (
      <BestBetsTakeover
        show={show}
        onHide={onHide}
        bestBets={bestBets}
        getTeamLogoUrl={getTeamLogoUrl}
        selectedSport={selectedSport}
      />
    );
  }

  // Desktop: Keep as modal
  return (
    <Modal show={show} onHide={onHide} className="best-bets-modal" style={{ '--bs-modal-width': '95vw' }}>
      <Modal.Header closeButton>
        <Modal.Title>Best Bets Right Now</Modal.Title>
      </Modal.Header>
      <Modal.Body className="best-bets-modal-body">
        {bestBets.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted">No best bets available right now.</p>
            <small>Best bets are highlighted when one book has significantly better odds.</small>
          </div>
        ) : (
          <div className="best-bets-dual-table-container">
            {/* Filter Row */}
            <div className="best-bets-filter-row">
              {isMainlineBestBets && (
                <div className="best-bets-filter-group">
                  <label htmlFor="market-filter">Market:</label>
                  <select
                    id="market-filter"
                    value={selectedMarket}
                    onChange={(e) => setSelectedMarket(e.target.value)}
                    className="best-bets-filter-select"
                  >
                    {marketTypes.map(mt => (
                      <option key={mt.value} value={mt.value}>{mt.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="best-bets-filter-group">
                <label htmlFor="point-filter">Line:</label>
                <select
                  id="point-filter"
                  value={selectedPoint}
                  onChange={(e) => setSelectedPoint(e.target.value)}
                  className="best-bets-filter-select"
                >
                  <option value="all">All Lines</option>
                  {uniquePoints.map(point => (
                    <option key={point} value={point}>{point}</option>
                  ))}
                </select>
              </div>
              <div className="best-bets-filter-group">
                <label htmlFor="book-filter">Book:</label>
                <select
                  id="book-filter"
                  value={selectedBook}
                  onChange={(e) => setSelectedBook(e.target.value)}
                  className="best-bets-filter-select"
                >
                  <option value="all">All Books ({bestBets.filter(bet => bet.book).length})</option>
                  {uniqueBooks.map(book => (
                    <option key={book} value={book}>
                      {book.charAt(0).toUpperCase() + book.slice(1)} ({bookCounts[book]})
                    </option>
                  ))}
                </select>
              </div>
              {(selectedSport === 'ncaab' || selectedSport === 'ncaaf') && uniqueConferences.length > 0 && (
                <div className="best-bets-filter-group">
                  <label htmlFor="conference-filter">Conference:</label>
                  <select
                    id="conference-filter"
                    value={selectedConference}
                    onChange={(e) => setSelectedConference(e.target.value)}
                    className="best-bets-filter-select"
                  >
                    <option value="all">All</option>
                    <option value="top25">Top 25</option>
                    {uniqueConferences.map(conf => (
                      <option key={conf} value={conf}>{conf}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="best-bets-filter-group">
                <label htmlFor="game-filter">Game:</label>
                <select
                  id="game-filter"
                  value={selectedGame}
                  onChange={(e) => setSelectedGame(e.target.value)}
                  className="best-bets-filter-select"
                >
                  <option value="all">All Games</option>
                  {uniqueGames.map(game => (
                    <option key={game} value={game}>{game}</option>
                  ))}
                </select>
              </div>
              {/* Tier Filter Icons */}
              <div className="best-bets-filter-group" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ marginRight: '4px' }}>Tiers:</label>
                <div
                  onClick={() => toggleTier('edge-one')}
                  title="Green tier (≥25% edge)"
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '4px',
                    backgroundColor: enabledTiers['edge-one'] ? 'green' : '#333',
                    border: enabledTiers['edge-one'] ? '2px solid #4ade80' : '2px solid #555',
                    cursor: 'pointer',
                    opacity: enabledTiers['edge-one'] ? 1 : 0.4,
                    transition: 'all 0.2s ease'
                  }}
                />
                <div
                  onClick={() => toggleTier('edge-two')}
                  title="Orange tier (≥15% edge)"
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '4px',
                    backgroundColor: enabledTiers['edge-two'] ? '#F59E0B' : '#333',
                    border: enabledTiers['edge-two'] ? '2px solid #fbbf24' : '2px solid #555',
                    cursor: 'pointer',
                    opacity: enabledTiers['edge-two'] ? 1 : 0.4,
                    transition: 'all 0.2s ease'
                  }}
                />
                <div
                  onClick={() => toggleTier('edge-three')}
                  title="Blue tier (≥5% edge)"
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '4px',
                    backgroundColor: enabledTiers['edge-three'] ? '#3B82F6' : '#333',
                    border: enabledTiers['edge-three'] ? '2px solid #60a5fa' : '2px solid #555',
                    cursor: 'pointer',
                    opacity: enabledTiers['edge-three'] ? 1 : 0.4,
                    transition: 'all 0.2s ease'
                  }}
                />
              </div>
              <div className="best-bets-odds-row">
                <div className="best-bets-filter-group best-bets-odds-filter">
                  <label>Min Odds:</label>
                  <input
                    type="range"
                    min={oddsMin}
                    max={oddsMax}
                    value={minOdds ?? oddsMin}
                    onChange={(e) => setMinOdds(parseInt(e.target.value, 10))}
                    className="best-bets-slider"
                  />
                  <input
                    type="number"
                    className="best-bets-odds-input"
                    value={minOddsInput || (minOdds ?? oddsMin)}
                    onChange={(e) => handleMinOddsInputChange(e.target.value)}
                  />
                </div>
                <div className="best-bets-filter-group best-bets-odds-filter">
                  <label>Max Odds:</label>
                  <input
                    type="range"
                    min={oddsMin}
                    max={oddsMax}
                    value={maxOdds ?? oddsMax}
                    onChange={(e) => setMaxOdds(parseInt(e.target.value, 10))}
                    className="best-bets-slider"
                  />
                  <input
                    type="number"
                    className="best-bets-odds-input"
                    value={maxOddsInput || (maxOdds ?? oddsMax)}
                    onChange={(e) => handleMaxOddsInputChange(e.target.value)}
                  />
                </div>
              </div>
              {/* Hide Bet365 toggle - NCAAB only */}
              {selectedSport === 'ncaab' && (
                <div className="best-bets-filter-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={hideBet365}
                      onChange={(e) => setHideBet365(e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span>Hide Bet365</span>
                  </label>
                  <div
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                      color: isDarkMode ? '#d1d5db' : '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    onClick={() => setShowBet365Tooltip(!showBet365Tooltip)}
                    onMouseEnter={() => setShowBet365Tooltip(true)}
                    onMouseLeave={() => setShowBet365Tooltip(false)}
                  >
                    ?
                  </div>
                  {showBet365Tooltip && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      marginTop: '8px',
                      padding: '10px 12px',
                      backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                      border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      width: '280px',
                      fontSize: '13px',
                      color: isDarkMode ? '#e5e7eb' : '#374151',
                      lineHeight: '1.4'
                    }}>
                      NCAAB alt lines for Bet365 have been inconsistent. To exclude them from Best Bets, click the toggle.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Fixed Header Table */}
            <div className="best-bets-header-container">
              <Table bordered size="sm" className="best-bets-header-table">
                <thead>
                  <tr>
                    <th className="best-bets-game-cell">Game</th>
                    <th className="best-bets-player-cell">Player</th>
                    <th className="best-bets-market-cell">Market</th>
                    {bookOrder.map(book => (
                      <th key={book} className="best-bets-odds-cell">
                        <img
                          src={`/logos/${book}.png`}
                          alt={book}
                          style={{ height: '16px' }}
                          title={book.charAt(0).toUpperCase() + book.slice(1)}
                        />
                      </th>
                    ))}
                    <th className="best-bets-advantage-cell">Adv</th>
                  </tr>
                </thead>
              </Table>
            </div>
            
            {/* Scrollable Body Table */}
            <div className="best-bets-body-container">
              <Table bordered size="sm" className="best-bets-body-table">
                <tbody>
                  {displayedBets.map((bet, index) => {
                    const bestBookKey = bet.book.toLowerCase();
                    
                    return (
                      <tr key={index} className={index % 2 === 0 ? 'best-bets-even-row' : 'best-bets-odd-row'}>
                        <td className="best-bets-game-cell" style={{ textAlign: 'center' }}>
                          <div>
                            <div>{bet.game}</div>
                            {bet.gameTime && (
                              <small className="text-muted d-block" style={{ marginTop: '2px', fontSize: '0.75rem' }}>
                                {formatGameTime(bet.gameTime)}
                              </small>
                            )}
                          </div>
                        </td>
                        <td className="best-bets-player-cell">
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            {bet.team && (
                              <img
                                src={bet.teamLogo || getTeamLogoUrl(bet.team)}
                                alt={bet.team}
                                className="best-bets-team-logo"
                                style={{ flexShrink: 0 }}
                              />
                            )}
                            {bet.market === 'Totals' && (bet.betType === 'Over' || bet.betType === 'Under') && (
                              <div style={{
                                flexShrink: 0,
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                color: bet.betType === 'Over' ? '#10B981' : '#EF4444'
                              }}>
{bet.betType === 'Over' ? '↑' : '↓'}
                              </div>
                            )}
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: '0.85rem', lineHeight: '1.2' }}>
                                {bet.player}
                                {bet.battingOrder && (
                                  <sup style={{
                                    fontSize: '0.6em',
                                    color: '#B3B3B3',
                                    fontWeight: 'normal',
                                    lineHeight: '1',
                                    marginLeft: '2px'
                                  }}>
                                    {bet.battingOrder}
                                  </sup>
                                )}
                                {bet.position && (
                                  <span style={{
                                    fontSize: '0.75rem',
                                    color: '#9CA3AF',
                                    fontWeight: 'normal',
                                    marginLeft: '4px'
                                  }}>
                                    ({bet.position})
                                  </span>
                                )}
                              </div>
                              {bet.awayPitcher && bet.homePitcher && bet.awayPitcher !== 'TBD' && bet.homePitcher !== 'TBD' && (
                                <div style={{ 
                                  fontSize: '0.65rem', 
                                  color: '#9CA3AF',
                                  lineHeight: '1.1'
                                }}>
                                  {bet.awayPitcher.split(' ').pop()} vs {bet.homePitcher.split(' ').pop()}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="best-bets-market-cell">
                          <div>
                            <div>{bet.market === 'Doubles' ? '2B' : bet.market === 'Home Runs' ? 'HR' : bet.market === 'Hits' ? 'H' : bet.market === 'RBIs' ? 'RBI' : bet.market === 'Runs' ? 'R' : bet.market === 'Hits + Runs + RBIs' ? 'H+R+R' : bet.market === 'Total Bases' ? 'TB' : bet.market === 'Stolen Bases' ? 'SB' : bet.market === 'Strikeouts' ? 'K' : bet.market === 'Total Touchdowns' ? 'TDs' : bet.market === 'Rush Attempts' ? 'Rush Att' : bet.market === 'Reception Yards' ? 'Rec Yards' : bet.market === 'First Goal Scorer' ? '1st Goal' : bet.market === 'Last Goal Scorer' ? 'Last Goal' : bet.market === 'Shots on Goal' ? 'Shots' : bet.market === 'First Team Basket' ? 'First Team' : bet.market === 'Points + Rebounds + Assists' ? 'P + R + A' : bet.market === 'Points + Rebounds' ? 'P + R' : bet.market === 'Points + Assists' ? 'P + A' : bet.market === 'Rebounds + Assists' ? 'R + A' : (bet.market || '').includes('Touchdown') ? bet.market.replace('Touchdown', 'TD') : bet.market}</div>
                            {bet.point !== null && bet.point !== undefined && (() => {
                              const bt = (bet.betType || '').toLowerCase();
                              const market = (bet.market || '').toLowerCase();
                              // Binary markets (yes/no) - check first before over/under
                              if (bet.point === 'yes' || bet.point === 'no') {
                                return <small className="text-muted d-block">{bet.point === 'yes' ? 'Yes' : 'No'}</small>;
                              }
                              // Over/Under markets: show Over/Under + point (full text on desktop)
                              if (bt === 'over' || bt === 'under') {
                                return <small className="text-muted d-block">{bt === 'over' ? 'Over' : 'Under'} {bet.point}</small>;
                              }
                              // Spreads: show +/- point
                              if (bt === 'underdog' || bt === 'favorite' || market.includes('spread')) {
                                const sign = bt === 'underdog' || bt === 'plus' ? '+' : '-';
                                return <small className="text-muted d-block">{sign}{Math.abs(bet.point)}</small>;
                              }
                              // Other markets with numeric points
                              if (typeof bet.point === 'number') {
                                return <small className="text-muted d-block">{bet.point}</small>;
                              }
                              return null;
                            })()}
                          </div>
                        </td>
                        {(() => {
                          // Find next best book when bet365 is best (only for NCAAB)
                          const isBet365Best = bestBookKey === 'bet365' && selectedSport === 'ncaab';
                          let nextBestBook = null;
                          let nextBestTier = null;

                          if (isBet365Best) {
                            const allBooks = Object.keys(bet.allOdds || {}).filter(key => !key.includes('_isAvailable'));
                            const otherOdds = allBooks
                              .filter(book => book !== 'bet365' && !isExcludedFromBestBook(book) && bet.allOdds[book] !== null && bet.allOdds[book] !== undefined)
                              .map(book => ({ book, odds: bet.allOdds[book] }))
                              .sort((a, b) => b.odds - a.odds);

                            if (otherOdds.length >= 1) {
                              nextBestBook = otherOdds[0].book;

                              // Calculate tier for 2nd best based on diff between 2nd and 3rd best
                              if (otherOdds.length >= 2) {
                                const nextBestOdds = otherOdds[0].odds;  // 2nd best (after bet365)
                                const thirdBestOdds = otherOdds[1].odds; // 3rd best

                                // Calculate diff using proper American odds formula
                                let diff;
                                if ((nextBestOdds > 0 && thirdBestOdds > 0) || (nextBestOdds < 0 && thirdBestOdds < 0)) {
                                  // Both same sign
                                  diff = Math.abs((nextBestOdds - thirdBestOdds) / thirdBestOdds);
                                } else if (nextBestOdds > 0 && thirdBestOdds < 0) {
                                  // Next best is positive, third best is negative (crossing even money)
                                  const nextDistance = nextBestOdds - 100;
                                  const thirdDistance = thirdBestOdds + 100;
                                  diff = Math.abs((nextDistance - thirdDistance) / thirdBestOdds);
                                } else {
                                  // Next best is negative, third best is positive
                                  const nextDistance = nextBestOdds + 100;
                                  const thirdDistance = thirdBestOdds - 100;
                                  diff = Math.abs((nextDistance - thirdDistance) / thirdBestOdds);
                                }

                                if (diff >= 0.25) {
                                  nextBestTier = 'edge-one';
                                } else if (diff >= 0.15) {
                                  nextBestTier = 'edge-two';
                                } else if (diff >= 0.05) {
                                  nextBestTier = 'edge-three';
                                }
                              }
                            }
                          }

                          return bookOrder.map(book => {
                            const odds = bet.allOdds[book];
                            const hasOdds = odds !== null && odds !== undefined;
                            const isBestBook = bestBookKey === book;
                            const isNextBestWhenBet365 = isBet365Best && book === nextBestBook;
                            const isAvailable = bet.allOdds[`${book}_isAvailable`] !== false;

                            // Determine tier class
                            let tierClass = '';
                            if (isBestBook && hasOdds) {
                              if (bet.tier === 'edge-three' && isDarkMode) {
                                tierClass = 'best-book-cell-edge-three-dark';
                              } else {
                                tierClass = `best-book-cell-${bet.tier}`;
                              }
                            } else if (isNextBestWhenBet365 && hasOdds && nextBestTier) {
                              if (nextBestTier === 'edge-three' && isDarkMode) {
                                tierClass = 'best-book-cell-edge-three-dark';
                              } else {
                                tierClass = `best-book-cell-${nextBestTier}`;
                              }
                            }

                            return (
                              <td
                                key={book}
                                className={`best-bets-odds-cell ${
                                  hasOdds ? tierClass : 'unavailable'
                                }`}
                                style={!isAvailable ? { opacity: 0.25 } : undefined}
                              >
                                {hasOdds ? (odds > 0 ? `+${odds}` : `${odds}`) : ''}
                              </td>
                            );
                          });
                        })()}
                        <td className="best-bets-advantage-cell">
                          <Badge 
                            className={`best-bets-badge tier-${bet.tier}`}
                          >
                            {(bet.advantage * 100).toFixed(1)}%
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="best-bets-filter-count" style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>
          Showing {displayedBets.length}{isLimited ? ` (max ${bestBetsMaxDesktop})` : ''} of {filteredBets.length} filtered / {bestBets.length} total
        </div>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BestBetsModal;